import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { createContainer } from 'unstated-next';

import { PASSWORD_RESET, PASSWORD_RESET_REQUEST, PASSWORD_RESET_TOKEN_VERIFICATION } from '../../config/apiPaths';
import { getUrlParam, getUrls } from '../../utils/url';

const usePasswordResetContainer = () => {
    const [passwordError, setPasswordError] = useState<number | null>(null);
    const [isValidToken, setIsValidToken] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFinished, setIsFinished] = useState(false);

    /**
     * In the background, check to see if the token in
     * the url for the password reset is valid.
     */
    useEffect(() => {
        const token = getUrlParam('token');

        if (!token) return;

        const verify = async () => {
            setIsLoading(true);

            const { api } = getUrls();

            try {
                await axios.get(`${api}${PASSWORD_RESET_TOKEN_VERIFICATION}?token=${token}`);

                setIsValidToken(true);
            } catch (err: any) {
                setIsValidToken(false);
            }

            setIsLoading(false);
        };

        verify();
    }, []);

    /**
     * Update a user password with a valid token
     */
    const resetPassword = useCallback(
        (password, token) => {
            if (isFinished) return;

            const reset = async () => {
                const { api } = getUrls();

                setIsLoading(true);

                try {
                    await axios.post(`${api}${PASSWORD_RESET}`, { password, token });

                    setIsFinished(true);
                } catch (err: any) {
                    setPasswordError(1);
                }

                setIsLoading(false);
            };

            reset();
        },
        [isFinished]
    );

    /**
     * Send a password reset request
     */
    const requestPasswordReset = useCallback(
        (email) => {
            if (isFinished) return;

            const request = async () => {
                const { api } = getUrls();

                setIsLoading(true);

                try {
                    await axios.post(`${api}${PASSWORD_RESET_REQUEST}`, { email });

                    setIsFinished(true);
                } catch (err: any) {
                    setPasswordError(1);
                }

                setIsLoading(false);
            };

            request();
        },
        [isFinished]
    );

    return {
        requestPasswordReset,
        resetPassword,
        passwordError,
        isValidToken,
        isLoading,
        isFinished
    };
};

const PasswordResetContainer = createContainer(usePasswordResetContainer);

export default PasswordResetContainer;
